import { useState } from 'react';

import { useTaskObjectQuery } from '@/hook';
import { Info, VFlex, VStack } from '@/package';
import type { Task, User } from '@/type';

import {
  calculateExtendClassData,
  calculateExtendClassDataWithBaseClass,
  calculateTotalClassData,
  filterUnknownClass,
} from '../Statistic';
import { SelectablePieChart } from '../Statistic';
import { StaticPieChart } from '../Statistic/StaticPieChart';
import { StatisticTable } from '../Statistic/StatisticTable';

export const TaskTotalLabeledObject = ({
  taskDetail,
  selectedMember,
}: {
  taskDetail: Task;
  selectedMember?: User['id'];
}) => {
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  const { data: taskObject } = useTaskObjectQuery(
    taskDetail.projectID,
    taskDetail.id,
    selectedMember,
    true,
  );

  const superClassList =
    taskDetail?.extendClass?.filter(
      item => filterUnknownClass(item) && item.classType !== 'Extend',
    ) ?? [];

  const taskTotalData = calculateTotalClassData(
    superClassList,
    taskObject?.statExtendClass,
  );

  const extendClassData =
    typeof selectedIndex === 'number' && superClassList[selectedIndex]
      ? calculateExtendClassData(
          superClassList[selectedIndex]?.extendClass,
          taskObject?.statExtendClass,
        )
      : [];

  const extendClassDataWithBaseClass =
    typeof selectedIndex === 'number' && taskTotalData[selectedIndex]
      ? calculateExtendClassDataWithBaseClass(
          taskTotalData[selectedIndex],
          extendClassData,
        )
      : [];

  const toggleSelect = (index: number) => {
    setSelectedIndex(index === selectedIndex ? null : index);
  };

  return (
    <VStack gap={0.5} className="w-full">
      <VFlex className="w-full" justify="between" items="center">
        <div className="text-headline-md font-bold text-secondary-500">
          Total labeled objects
        </div>
        <VFlex items="center" justify="end" gap={0.25} className="flex-1">
          <Info className="size-4 text-State-info" />
          <div className="text-body-s">
            Clicking a pie chart slice or table item in the base class also
            allows you to view statistics for its extension classes.
          </div>
        </VFlex>
      </VFlex>
      <VFlex gap={2} items="center" justify="center">
        <SelectablePieChart
          pieData={taskTotalData}
          selectedValue={selectedIndex}
          onSelect={toggleSelect}
        />
        <StatisticTable
          data={taskTotalData}
          selectedValue={selectedIndex}
          onRowClick={toggleSelect}
        />
      </VFlex>

      {typeof selectedIndex === 'number' &&
        extendClassData.length > 0 &&
        taskTotalData[selectedIndex] && (
          <VFlex gap={2} items="center" justify="center">
            <StaticPieChart
              pieData={extendClassDataWithBaseClass}
              onClose={() => setSelectedIndex(null)}
            />
            <StatisticTable data={extendClassDataWithBaseClass} />
          </VFlex>
        )}
    </VStack>
  );
};

TaskTotalLabeledObject.displayName = 'TaskTotalLabeledObject';
